import { useMemo } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import V2PageHeader from "components/V2PageHeader";
import CompetitorSelector from "components/EntitySelector/CompetitorSelector";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import BarChart from "components/charts/BarChart";

// M005/S02 reusable components
import InlineBriefing from "components/InlineBriefing";
import useInlineBriefingData from "components/InlineBriefing/useInlineBriefingData";

// M005/S03 new components
import ComparativeStatsStrip from "components/ComparativeStatsStrip";
import PostFeedCard from "components/PostFeedCard";

// Existing digital-presence data hooks (reused as-is)
import { useBarChartData } from "pages/digital-presence/data/social-media/barChartData";
import { getBarChartOptions } from "pages/digital-presence/data/social-media/barChartOptions";
import ContentDistribution from "pages/digital-presence/data/social-media/ContentDistribution";

// v2-specific hooks and components
import usePostFeed, { ALL_COMPETITORS_ID } from "./usePostFeed";
import useIndustryBenchmark from "./useIndustryBenchmark";
import IndustryBenchmarkStrip from "./IndustryBenchmarkStrip";

// Context
import { useAnalytics } from "context/AnalyticsContext";
import { ACCENT_COLORS } from "utils/designTokens";
import { getCategoryColor } from "pages/digital-presence/data/social-media/categoryColorMapping";

// The 6 ContentFocus categories for the filter strip
const CONTENT_FOCUS_CATEGORIES = [
  "Clinical Evidence",
  "Product & Technology",
  "Education & Case Sharing",
  "Events & Congresses",
  "Company & Strategy",
  "Other",
];

// Bar chart colours — match the existing chart's fill order so panels are consistent
// barChartOptions fill: ["#2CD9FF", "#0075FF"] → index 0 = competitor, index 1 = client
const COMPETITOR_COLOR = "#2CD9FF";
const CLIENT_COLOR = "#0075FF";

// Relative date formatting: "Today", "Yesterday", "2 weeks ago", etc.
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // Accept both "2026-03-24" (date-only) and "2026-03-24T18:36:34" (datetime)
    const d = dateStr.includes("T") ? new Date(dateStr) : new Date(dateStr + "T12:00:00");
    if (isNaN(d.getTime())) return dateStr;
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return "Last week";
    if (diffDays < 21) return "2 weeks ago";
    if (diffDays < 28) return "3 weeks ago";
    const diffMonths = Math.round(diffDays / 30);
    if (diffMonths <= 1) return "1 month ago";
    if (diffMonths <= 6) return `${diffMonths} months ago`;
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  } catch {
    return dateStr;
  }
};

// ── Module-level sub-components (must be outside render to avoid remount on every render) ──

function PillButton({ label, active, color, onClick }) {
  return (
    <VuiBox
      onClick={onClick}
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: "14px",
        border: `1px solid ${active ? (color || ACCENT_COLORS.primary) : "rgba(255,255,255,0.12)"}`,
        background: active
          ? `${color || ACCENT_COLORS.primary}22`
          : "rgba(255,255,255,0.05)",
        cursor: "pointer",
        flexShrink: 0,
        transition: "all 0.18s ease",
        userSelect: "none",
        "&:hover": {
          background: active
            ? `${color || ACCENT_COLORS.primary}30`
            : "rgba(255,255,255,0.09)",
        },
      }}
    >
      <VuiTypography
        variant="caption"
        color="white"
        fontWeight={active ? "bold" : "regular"}
        sx={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}
      >
        {label}
      </VuiTypography>
    </VuiBox>
  );
}

function SortToggle({ sortMode, setSortMode }) {
  return (
    <VuiBox display="flex" gap={0.75}>
      {["latest", "engaged"].map((mode) => (
        <VuiBox
          key={mode}
          onClick={() => setSortMode(mode)}
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: "10px",
            background: sortMode === mode ? "rgba(37,99,235,0.22)" : "rgba(255,255,255,0.05)",
            border: `1px solid ${sortMode === mode ? ACCENT_COLORS.primary : "rgba(255,255,255,0.1)"}`,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <VuiTypography
            variant="caption"
            sx={{
              fontSize: "0.73rem",
              color: sortMode === mode ? "#93c5fd" : "rgba(255,255,255,0.45)",
              fontWeight: sortMode === mode ? "bold" : "regular",
            }}
          >
            {mode === "latest" ? "Latest" : "Most Engaged"}
          </VuiTypography>
        </VuiBox>
      ))}
    </VuiBox>
  );
}

/**
 * DigitalPresenceV2
 *
 * Route: /v2/competitor-insights/:platform   adminOnly: true
 *
 * Default selection: "All Competitors" — shows a merged post feed from all competitors.
 * Selecting a specific competitor switches to single-competitor mode with
 * AI brief, comparative stats, and side-by-side content panels.
 */
function DigitalPresenceV2() {
  const { platform = "linkedin" } = useParams();
  const { selectedCompetitor, companyId, selectedPresetKey, competitors, companyName } = useAnalytics();

  const isAllView = selectedCompetitor === ALL_COMPETITORS_ID;

  const competitorName = useMemo(() => {
    if (isAllView || !selectedCompetitor || !competitors) return null;
    return competitors.find((c) => c.id === selectedCompetitor)?.name ?? null;
  }, [selectedCompetitor, competitors, isAllView]);

  const clientName = companyName || "Your Company";

  // Competitor IDs for the all-view parallel fetch
  const allCompetitorIds = useMemo(
    () => (competitors || []).map((c) => c.id),
    [competitors]
  );

  // Company name lookup for all-view cards (id → name)
  const competitorNameMap = useMemo(() => {
    const map = {};
    (competitors || []).forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [competitors]);

  // ── ZONE 0: Industry benchmark (all-view only) ───────────────────────────
  const {
    clientStats: benchClientStats,
    industryAvg,
    competitorStats: benchCompetitorStats,
    loading: benchLoading,
  } = useIndustryBenchmark(
    platform,
    isAllView ? companyId : null,   // only fetch in all-view
    isAllView ? allCompetitorIds : [],
    selectedPresetKey
  );

  // ── ZONE 1: AI Brief ─────────────────────────────────────────────────────
  // All-view: market-level brief across all competitors
  // Single-competitor: per-competitor quick-catchup
  const briefingEndpoint = isAllView
    ? (companyId ? "/v1/reports/market-linkedin-brief" : null)
    : (selectedCompetitor ? "/v1/reports/quick-catchup" : null);

  const briefingParams = useMemo(() => {
    if (isAllView) return { companyId };
    return { competitorId: selectedCompetitor };
  }, [isAllView, companyId, selectedCompetitor]);

  const { briefing, loading: briefingLoading, error: briefingError } = useInlineBriefingData(
    briefingEndpoint,
    briefingParams
  );
  const briefingText = briefing?.competitorBriefing ?? briefing;

  // ── ZONE 3: Content calendar — split series per company ──────────────────
  const { barChartData, categories: barCategories, loading: barLoading } =
    useBarChartData(
      companyId,
      isAllView ? null : selectedCompetitor,
      platform,
      selectedPresetKey
    ) || {};
  // series[0] = client (companyId), series[1] = competitor — matches backend build order
  const clientBarData = useMemo(
    () => (barChartData?.length > 0 ? [barChartData[0]] : []),
    [barChartData]
  );
  const competitorBarData = useMemo(
    () => (barChartData?.length > 1 ? [barChartData[1]] : []),
    [barChartData]
  );
  const competitorBarOptions = useMemo(
    () => ({ ...getBarChartOptions(barCategories, selectedPresetKey), fill: { colors: [COMPETITOR_COLOR] } }),
    [barCategories, selectedPresetKey]
  );
  const clientBarOptions = useMemo(
    () => ({ ...getBarChartOptions(barCategories, selectedPresetKey), fill: { colors: [CLIENT_COLOR] } }),
    [barCategories, selectedPresetKey]
  );

  // ── ZONE 4: Post feed ─────────────────────────────────────────────────────
  const {
    posts,
    loading: postsLoading,
    error: postsError,
    selectedCategory,
    setSelectedCategory,
    sortMode,
    setSortMode,
    hasMore,
    loadMore,
    total: totalPosts,
  } = usePostFeed(selectedCompetitor, platform, selectedPresetKey, allCompetitorIds);

  return (
    <DashboardLayout navbarHeight={88}>
      <DashboardNavbar noSelectors />
      <VuiBox pt={0} pb={3}>

        {/* Page header — title left, competitor selector right */}
        <V2PageHeader
          title="LinkedIn Watch"
          subtitle={!isAllView && competitorName ? competitorName : null}
        >
          <CompetitorSelector
            customOptions={[{ id: ALL_COMPETITORS_ID, name: "All Competitors" }]}
          />
        </V2PageHeader>

        {/* ── ZONE 0: Industry Benchmark Strip (all-view only) ── */}
        {isAllView && (
          <VuiBox mb={3}>
            <IndustryBenchmarkStrip
              clientStats={benchClientStats}
              industryAvg={industryAvg}
              clientName={clientName}
              competitorCount={benchCompetitorStats.length}
              loading={benchLoading}
            />
          </VuiBox>
        )}

        {/* ── ZONE 1: Comparative Stats (single-competitor only) — above the brief for consistency ── */}
        {!isAllView && (
          <VuiBox mb={3}>
            <ComparativeStatsStrip
              platform={platform}
              competitorId={selectedCompetitor}
              clientId={companyId}
              presetKey={selectedPresetKey}
              competitorName={competitorName}
              clientName={clientName}
            />
          </VuiBox>
        )}

        {/* ── ZONE 2: AI Brief (all-view and single-competitor) ── */}
        <VuiBox mb={3}>
          <InlineBriefing
            briefing={briefingText}
            loading={briefingLoading}
            error={briefingError}
            title={isAllView
              ? "What's new on LinkedIn?"
              : (competitorName
                  ? `What's new with ${competitorName}'s LinkedIn?`
                  : "What's new on LinkedIn?")}
            minHeight={180}
          />
        </VuiBox>

        {/* ── ZONE 3: Side-by-side panels (single-competitor only) ── */}
        {!isAllView && (
          <VuiBox mb={3}>
            <Grid container spacing={3}>
              {[
                { id: selectedCompetitor, name: competitorName, isClient: false, barData: competitorBarData, barOpts: competitorBarOptions, dotColor: COMPETITOR_COLOR },
                { id: companyId, name: clientName, isClient: true, barData: clientBarData, barOpts: clientBarOptions, dotColor: CLIENT_COLOR },
              ].map((company) => (
                <Grid key={company.id} item xs={12} lg={6}>
                  <GlassCard sx={{ "&:hover": { transform: "none", boxShadow: "none" } }}>
                    <VuiBox display="flex" alignItems="center" gap={1} mb={1.5}>
                      <VuiBox sx={{ width: 7, height: 7, borderRadius: "50%", background: company.dotColor, flexShrink: 0 }} />
                      <VuiTypography variant="h6" color="white" fontWeight="bold" sx={{ fontSize: "0.88rem" }}>
                        {company.name}
                      </VuiTypography>
                      {company.isClient && (
                        <VuiBox sx={{ px: 0.75, py: 0.1, borderRadius: "4px", background: "rgba(0,117,255,0.15)", border: "1px solid rgba(0,117,255,0.3)" }}>
                          <VuiTypography variant="caption" sx={{ fontSize: "0.62rem", color: "#7eb8ff", fontWeight: "700" }}>YOU</VuiTypography>
                        </VuiBox>
                      )}
                    </VuiBox>

                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.68rem", letterSpacing: "0.07em", textTransform: "uppercase", display: "block", mb: 1 }}>
                      Content Calendar
                    </VuiTypography>
                    <VuiBox mb={2} sx={{ height: 180 }}>
                      {barLoading ? (
                        <StandardLoadingIndicator containerHeight="180px" />
                      ) : company.barData?.length > 0 ? (
                        <BarChart
                          barChartData={company.barData}
                          barChartOptions={company.barOpts}
                          key={`bar-${company.id}-${selectedPresetKey}`}
                        />
                      ) : (
                        <StandardMessageDisplay type="noData" title="No activity" containerHeight="180px" />
                      )}
                    </VuiBox>

                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.68rem", letterSpacing: "0.07em", textTransform: "uppercase", display: "block", mb: 1 }}>
                      Content Breakdown
                    </VuiTypography>
                    <ContentDistribution
                      platform={platform}
                      companyId={company.id}
                      chartType="postType"
                      isCompanyView={company.isClient}
                      key={`pie-${company.id}-${selectedPresetKey}`}
                    />
                  </GlassCard>
                </Grid>
              ))}
            </Grid>
          </VuiBox>
        )}

        {/* ── ZONE 4: Post Feed ── */}
        <VuiBox mb={3}>
          {/* Filter pills + sort toggle */}
          <VuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1.5}
            mb={2}
          >
            <VuiBox display="flex" flexWrap="wrap" gap={1}>
              <PillButton
                label="All"
                active={!selectedCategory}
                onClick={() => setSelectedCategory(null)}
              />
              {CONTENT_FOCUS_CATEGORIES.map((cat) => (
                <PillButton
                  key={cat}
                  label={cat}
                  active={selectedCategory === cat}
                  color={getCategoryColor(cat)}
                  onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                />
              ))}
            </VuiBox>
            <VuiBox display="flex" alignItems="center" gap={2}>
              {!postsLoading && (
                <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem" }}>
                  {totalPosts} post{totalPosts !== 1 ? "s" : ""}
                </VuiTypography>
              )}
              <SortToggle sortMode={sortMode} setSortMode={setSortMode} />
            </VuiBox>
          </VuiBox>

          {/* Post cards */}
          {postsError ? (
            <StandardMessageDisplay
              type="error"
              title="Could not load posts"
              message="Please try refreshing."
              containerHeight="200px"
            />
          ) : posts.length === 0 && !postsLoading ? (
            <StandardMessageDisplay
              type="noData"
              title="No posts found"
              message={selectedCategory
                ? `No ${selectedCategory} posts this period.`
                : "No posts for the selected period."}
              containerHeight="200px"
            />
          ) : (
            <VuiBox sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {posts.map((post, i) => {
                const postCompanyId = post._companyId || selectedCompetitor;
                const postCompanyName = isAllView
                  ? (competitorNameMap[postCompanyId] || "Competitor")
                  : (competitorName || "Competitor");
                return (
                  <PostFeedCard
                    key={post.id ?? post.url ?? i}
                    date={post.timestamp}
                    dateLabel={formatRelativeDate(post.timestamp)}
                    companyName={postCompanyName}
                    companyId={postCompanyId}
                    category={post.analysis?.type}
                    engagements={(post.likes || 0) + (post.comments || 0) + (post.shares || 0)}
                    vsAvg={post.vsAvg}
                    summary={post.analysis?.summary}
                    url={post.url}
                  />
                );
              })}

              {postsLoading && (
                <VuiBox display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={22} sx={{ color: ACCENT_COLORS.highlight }} />
                </VuiBox>
              )}

              {hasMore && !postsLoading && (
                <VuiBox display="flex" justifyContent="center" pt={1}>
                  <VuiBox
                    onClick={loadMore}
                    sx={{
                      px: 3, py: 0.875,
                      borderRadius: "20px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.05)",
                      cursor: "pointer",
                      "&:hover": { background: "rgba(255,255,255,0.09)", borderColor: "rgba(255,255,255,0.25)" },
                    }}
                  >
                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem" }}>
                      Load more posts
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              )}
            </VuiBox>
          )}
        </VuiBox>

      </VuiBox>
    </DashboardLayout>
  );
}

export default DigitalPresenceV2;
